import React, { useState, useEffect, useRef } from 'react';
import { getDevices, getPositions, getDrivers, updateDevice, getGeofences, createGeofence, deleteGeofence, linkGeofenceToDevice, geocode, getAllDevices, getStoredMe } from '../traccar';
import { getVehiclePhotos, savePhoto, removePhoto, fileToResizedDataUrl, SIDE_SLOTS, INSIDE_SLOTS } from '../photoStore';
import { playOpen, playClose } from '../clickSound';
import VehicleHistory from '../VehicleHistory';
import { VEHICLE_YEARS, VEHICLE_MAKES, VEHICLE_COLORS, modelsForMake } from '../vehicleOptions';
import PhotoPicker from '../PhotoPicker';
import { codeForDevice, qrUrlFor, brokerAddUrl, qrUrlForFamily, familyAddUrl } from '../carCode';
import { getBrokerLinks, setBrokerLinkStatus, removeBrokerLink, pendingLinks, setBrokerLinkTracking } from '../brokerLink';
import { getMemberLinks, setMemberLinkStatus, removeMemberLink, pendingMemberLinks, approvedForMember, shareIdentity } from '../memberLink';
import AddSharedVehicle from '../AddSharedVehicle';
import { getRentalSettings, saveRentalSettings } from '../rentalStore';
import AddressAutocomplete from '../AddressAutocomplete';

const SLOT_LABELS = { front: 'Front', left: 'Left side', right: 'Right side', back: 'Back', inside0: 'Inside 1', inside1: 'Inside 2', inside2: 'Inside 3', inside3: 'Inside 4' };

// Traccar's raw status is the connection state (drops between reports).
// Derive a meaningful status from how recently the device actually reported.
function liveStatus(device) {
  const last = device.lastUpdate ? new Date(device.lastUpdate).getTime() : 0;
  const mins = (Date.now() - last) / 60000;
  if (mins < 15) return 'online';
  if (mins < 120) return 'idle';
  return 'offline';
}
// native app = dark glass skin
const IS_NATIVE = typeof window !== 'undefined' && !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
// The device profile MODAL is a white card in both web and native, so its text
// must stay dark or it washes out on white (this was the "hard to read" bug).
const TXT = '#0f172a';
const TXT_MUTED = '#64748b';
// Labels that sit on the PAGE background (grid tiles + shared-place chips) do
// follow the theme — light on the dark native page, dark on the white web page.
const TILE_TXT = IS_NATIVE ? '#e8ebf5' : '#0f172a';
const TILE_MUTED = IS_NATIVE ? '#9aa0b4' : '#94a3b8';
// the green "add a place to all vehicles" panel + its chips
const GREEN_BG = IS_NATIVE ? 'rgba(34,197,94,0.10)' : '#f0fdf4';
const GREEN_BD = IS_NATIVE ? 'rgba(34,197,94,0.28)' : '#bbf7d0';
const CHIP_BG = IS_NATIVE ? 'rgba(255,255,255,0.08)' : 'white';

const statusChip = (status) => ({
  online: { text: 'REPORTING', bg: '#f0fdf4', color: '#15803d' },
  idle: { text: 'STANDBY', bg: '#fffbeb', color: '#b45309' },
  offline: { text: 'NO SIGNAL', bg: '#fef2f2', color: '#b91c1c' },
}[status] || { text: 'NO SIGNAL', bg: '#fef2f2', color: '#b91c1c' });

// One photo slot: shows the photo, or an "add" box; click to take a photo or
// upload from the library.
function PhotoSlot({ label, photo, onUpload, onRemove }) {
  return (
    <div style={{ position: 'relative' }}>
      <PhotoPicker onPhoto={onUpload} onFileProcess={fileToResizedDataUrl} wrapStyle={{ display: 'block' }}>
        <div
          style={{
            height: '90px', width: '100%', boxSizing: 'border-box', borderRadius: '10px', cursor: 'pointer', overflow: 'hidden',
            border: photo ? '1px solid #e2e8f0' : '2px dashed #cbd5e1',
            background: photo ? `url(${photo}) center/cover` : '#f8fafc',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          title={photo ? `Replace ${label} photo` : `Add ${label} photo`}
        >
          {!photo && <span style={{ fontSize: '12px', color: TXT_MUTED, textAlign: 'center' }}>📷 {label}</span>}
        </div>
      </PhotoPicker>
      {photo && (
        <>
          <span style={{
            position: 'absolute', left: '6px', bottom: '6px', fontSize: '10px', fontWeight: 700,
            background: 'rgba(15,23,42,0.7)', color: 'white', padding: '2px 7px', borderRadius: '999px',
          }}>{label}</span>
          <button
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            style={{
              position: 'absolute', top: '5px', right: '5px', width: '22px', height: '22px', padding: 0,
              borderRadius: '50%', background: 'rgba(15,23,42,0.7)', color: 'white', fontSize: '12px', lineHeight: 1,
            }}
            title="Remove photo"
          >
            ×
          </button>
        </>
      )}
    </div>
  );
}

export default function VehiclesPage() {
  const [devices, setDevices] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0); // bump to re-pull after adding a shared car
  const [rentalTick, setRentalTick] = useState(0); // re-render when the rentals toggle changes
  const [positions, setPositions] = useState([]);
  const [thumbs, setThumbs] = useState({}); // deviceId -> front photo for the grid
  const [openId, setOpenId] = useState(null);
  const [photos, setPhotos] = useState({});
  const [error, setError] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [search, setSearch] = useState('');
  const [searchField, setSearchField] = useState('all');
  const [historyDevice, setHistoryDevice] = useState(null);
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [qrBig, setQrBig] = useState(false);
  // saved places (⭐ geofences) — power the "did the car arrive at X" chat + arrived-safe alerts
  const [devPlaces, setDevPlaces] = useState([]); // ⭐ geofences linked to the open car
  const [placeInput, setPlaceInput] = useState({ name: '', address: '', lat: null, lng: null });
  const [placeBusy, setPlaceBusy] = useState(false);
  const [allPlace, setAllPlace] = useState({ name: '', address: '', lat: null, lng: null }); // add-to-all-cars form
  const [allBusy, setAllBusy] = useState(false);
  const [allMsg, setAllMsg] = useState('');
  const [allPlaces, setAllPlaces] = useState([]); // every ⭐ place across all cars (for the list)

  const placeName = (g) => (g.name || '').replace(/^⭐\s*/, '');

  // load the ⭐ saved places linked to a specific car
  const loadDevPlaces = async (id) => {
    try { const gs = await getGeofences(id); setDevPlaces((gs || []).filter((g) => (g.name || '').startsWith('⭐'))); }
    catch { setDevPlaces([]); }
  };
  // load ALL ⭐ saved places (for the list under the green box)
  const loadAllPlaces = async () => {
    try { const gs = await getGeofences(); setAllPlaces((gs || []).filter((g) => (g.name || '').startsWith('⭐'))); }
    catch { setAllPlaces([]); }
  };
  useEffect(() => { if (openId) loadDevPlaces(openId); else setDevPlaces([]); }, [openId]);
  useEffect(() => { loadAllPlaces(); }, []);

  // resolve coords: use the ones picked from autocomplete, else geocode the text
  const resolveCoords = async (form) => {
    if (form.lat != null && form.lng != null) return { lat: form.lat, lng: form.lng };
    return geocode({ address: (form.address || '').trim() });
  };

  // add one saved place to the currently-open car
  const addCarPlace = async () => {
    const address = (placeInput.address || '').trim();
    if (!address || placeBusy) return;
    setPlaceBusy(true);
    try {
      const pt = await resolveCoords(placeInput);
      if (!pt) { alert('Couldn’t find that address. Pick one from the suggestions, or add a city, state, or ZIP.'); setPlaceBusy(false); return; }
      const label = (placeInput.name || '').trim() || address;
      const g = await createGeofence(`⭐ ${label}`, pt.lat, pt.lng, 250);
      if (g && g.id) await linkGeofenceToDevice(openId, g.id);
      setPlaceInput({ name: '', address: '', lat: null, lng: null });
      await loadDevPlaces(openId); await loadAllPlaces();
    } catch { alert('Couldn’t save that place. Please try again.'); }
    setPlaceBusy(false);
  };

  const removeCarPlace = async (g) => {
    try { await deleteGeofence(g.id); } catch { /* ignore */ }
    await loadDevPlaces(openId); await loadAllPlaces();
  };

  // add one saved place to EVERY car (each gets its own copy, removable per car)
  const addPlaceAllCars = async () => {
    const address = (allPlace.address || '').trim();
    if (!address || allBusy) return;
    setAllBusy(true); setAllMsg('');
    try {
      const pt = await resolveCoords(allPlace);
      if (!pt) { setAllMsg('❌ Couldn’t find that address. Pick one from the suggestions, or add a city, state, or ZIP.'); setAllBusy(false); return; }
      const label = (allPlace.name || '').trim() || address;
      let n = 0;
      for (const d of devices) {
        const g = await createGeofence(`⭐ ${label}`, pt.lat, pt.lng, 250).catch(() => null);
        if (g && g.id) { await linkGeofenceToDevice(d.id, g.id).catch(() => {}); n += 1; }
      }
      setAllPlace({ name: '', address: '', lat: null, lng: null });
      setAllMsg(`✅ Added “${label}” to ${n} vehicle${n === 1 ? '' : 's'}.`);
      await loadAllPlaces();
      if (openId) loadDevPlaces(openId);
    } catch { setAllMsg('❌ Couldn’t add to all vehicles. Please try again.'); }
    setAllBusy(false);
  };

  // remove every copy of a place (by name) across all cars
  const removePlaceGroup = async (name) => {
    const gids = allPlaces.filter((g) => placeName(g) === name).map((g) => g.id);
    for (const id of gids) { await deleteGeofence(id).catch(() => {}); }
    await loadAllPlaces();
    if (openId) loadDevPlaces(openId);
  };

  // group the flat ⭐ list by name → [{ name, count }]
  const placeGroups = Object.values(allPlaces.reduce((acc, g) => {
    const nm = placeName(g);
    acc[nm] = acc[nm] || { name: nm, count: 0 };
    acc[nm].count += 1;
    return acc;
  }, {})).sort((a, b) => a.name.localeCompare(b.name));

  const decideBroker = async (dev, brokerId, status) => {
    try { const updated = await setBrokerLinkStatus(dev, brokerId, status); setDevices((ds) => ds.map((d) => (d.id === dev.id ? updated : d))); }
    catch (e) { alert(`Couldn't update: ${e.message}`); }
  };
  const revokeBroker = async (dev, brokerId) => {
    try { const updated = await removeBrokerLink(dev, brokerId); setDevices((ds) => ds.map((d) => (d.id === dev.id ? updated : d))); }
    catch (e) { alert(`Couldn't update: ${e.message}`); }
  };
  // toggle rentals: keep the local flag (drives owner's Rentals tab) AND mirror it
  // onto the device so brokers/other viewers see the car's availability.
  const toggleRental = async (dev, on) => {
    saveRentalSettings(dev.id, { enabled: on });
    setRentalTick((n) => n + 1);
    try {
      const updated = { ...dev, attributes: { ...(dev.attributes || {}), rentalEnabled: on } };
      await updateDevice(updated);
      setDevices((ds) => ds.map((d) => (d.id === dev.id ? updated : d)));
    } catch { /* attribute sync failed; local flag still applies for the owner */ }
  };
  const decideMember = async (dev, memberId, status) => {
    try { const updated = await setMemberLinkStatus(dev, memberId, status); setDevices((ds) => ds.map((d) => (d.id === dev.id ? updated : d))); }
    catch (e) { alert(`Couldn't update: ${e.message}`); }
  };
  const revokeMember = async (dev, memberId) => {
    try { const updated = await removeMemberLink(dev, memberId); setDevices((ds) => ds.map((d) => (d.id === dev.id ? updated : d))); }
    catch (e) { alert(`Couldn't update: ${e.message}`); }
  };
  const toggleBrokerTracking = async (dev, brokerId, on) => {
    try { const updated = await setBrokerLinkTracking(dev, brokerId, on); setDevices((ds) => ds.map((d) => (d.id === dev.id ? updated : d))); }
    catch (e) { alert(`Couldn't update: ${e.message}`); }
  };

  const shareCarCode = async (dev) => {
    const nm = (dev.attributes || {}).displayName || dev.name;
    const url = brokerAddUrl(codeForDevice(dev));
    const data = { title: 'Add my car on TagAlong', text: `Add ${nm} to your TagAlong rental fleet. Code: ${codeForDevice(dev)}`, url };
    if (navigator.share) { try { await navigator.share(data); return; } catch (e) { if (e && e.name === 'AbortError') return; } }
    try { await navigator.clipboard.writeText(url); alert('Share link copied — send it to your broker.'); } catch { alert(url); }
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const ident = shareIdentity(getStoredMe());
        const [devs, poss, drs, allForShare] = await Promise.all([getDevices(), getPositions(), getDrivers().catch(() => []), ident ? getAllDevices().catch(() => []) : Promise.resolve([])]);
        if (cancelled) return;
        // merge in cars other owners shared with me (approved)
        let merged = devs;
        if (ident) {
          const shared = approvedForMember(allForShare, ident.id);
          const have = new Set(devs.map((x) => String(x.id)));
          merged = [...devs, ...shared.filter((x) => !have.has(String(x.id)))];
        }
        setDevices(merged);
        setPositions(poss);
        setDrivers(drs);
        setError(null);
        const t = {};
        await Promise.all(merged.map(async (d) => {
          const p = await getVehiclePhotos(d.id);
          t[d.id] = p.front;
        }));
        if (!cancelled) setThumbs(t);
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    };
    load();
    const timer = setInterval(load, 15000);
    return () => { cancelled = true; clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const openProfile = async (id) => {
    if (openId === id) { playClose(); setOpenId(null); return; }
    playOpen();
    setOpenId(id);
    setPhotos(await getVehiclePhotos(id));
  };

  // arriving from an "alerts & limits" link (?device=IMEI) → open that profile
  const autoOpened = useRef(false);
  useEffect(() => {
    if (autoOpened.current || !devices.length) return;
    const m = (window.location.search || '').match(/[?&]device=([^&]+)/);
    if (m) { const dev = devices.find((d) => String(d.uniqueId) === decodeURIComponent(m[1])); if (dev) { autoOpened.current = true; openProfile(dev.id); } }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devices]);
  const closeProfile = () => { playClose(); setOpenId(null); };

  const upload = async (slot, dataUrl) => {
    await savePhoto(openId, slot, dataUrl);
    setPhotos((p) => ({ ...p, [slot]: dataUrl }));
    if (slot === 'front') setThumbs((t) => ({ ...t, [openId]: dataUrl }));
  };

  const remove = async (slot) => {
    await removePhoto(openId, slot);
    setPhotos((p) => ({ ...p, [slot]: null }));
    if (slot === 'front') setThumbs((t) => ({ ...t, [openId]: null }));
  };

  const device = devices.find((d) => d.id === openId);
  const position = positions.find((p) => p.deviceId === openId);
  const a = (position && position.attributes) || {};

  const saveDeviceAttr = async (patch) => {
    const updated = { ...device, attributes: { ...(device.attributes || {}), ...patch } };
    try { await updateDevice(updated); setDevices((ds) => ds.map((d) => (d.id === device.id ? updated : d))); }
    catch (e) { alert(`Couldn't save: ${e.message}`); }
  };

  const saveDeviceName = async () => {
    const name = nameDraft.trim();
    setRenaming(false);
    if (!device) return;
    const cur = (device.attributes || {}).displayName || '';
    if (name === cur) return;
    // Store the customer's chosen name in attributes.displayName and LEAVE
    // device.name (the account/serial — e.g. "7440-6474") untouched as the
    // permanent backend reference. Clearing the field reverts to that reference.
    const attributes = { ...(device.attributes || {}) };
    if (name) attributes.displayName = name; else delete attributes.displayName;
    const updated = { ...device, attributes };
    try { await updateDevice(updated); setDevices((ds) => ds.map((d) => (d.id === device.id ? updated : d))); }
    catch (e) { alert(`Couldn't rename: ${e.message}`); }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>🚛 Vehicles</h1>
        <p>{error ? `🔴 ${error}` : 'Every device you add in the tracking system appears here automatically. Click a vehicle to open its profile and photos.'}</p>
      </div>

      {/* search */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '18px', flexWrap: 'wrap', alignItems: 'center' }}>
        <select style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', width: '170px' }} value={searchField} onChange={(e) => setSearchField(e.target.value)}>
          <option value="all">Search everything</option>
          <option value="name">Vehicle name</option>
          <option value="vin">VIN</option>
          <option value="imei">IMEI</option>
          <option value="driver">Assigned driver</option>
          <option value="status">Status (online/offline)</option>
        </select>
        <input
          style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', flex: 1, minWidth: '220px' }}
          placeholder="🔍 Type to search vehicles…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && <button style={{ background: '#e2e8f0', color: TXT, padding: '7px 12px', borderRadius: '6px', fontWeight: 600, fontSize: '12px' }} onClick={() => setSearch('')}>Clear</button>}
        <AddSharedVehicle onAdded={() => setRefreshKey((n) => n + 1)} buttonLabel="➕ Add a vehicle" />
      </div>

      {/* add a saved place (geofence) to EVERY vehicle at once */}
      <div style={{ border: `1px solid ${GREEN_BD}`, borderRadius: '16px', padding: '14px 16px', marginBottom: '18px', background: GREEN_BG }}>
        <div style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: '13px', color: '#15803d', marginBottom: '2px' }}>📍 Add a place to all vehicles</div>
        <div style={{ fontSize: '11px', color: '#166534', marginBottom: '9px' }}>Adds one address (Home, Office, Yard…) to every car. You’ll get an “arrived” alert when any car parks there, and can ask the car “did it get to Home?”. Each car keeps its own copy — remove it per car from that car’s profile.</div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input style={{ padding: '8px 10px', border: '1px solid #86efac', borderRadius: '6px', fontSize: '13px', width: '150px' }}
            placeholder="Name (e.g. Home)" value={allPlace.name} onChange={(e) => setAllPlace((s) => ({ ...s, name: e.target.value }))} />
          <AddressAutocomplete
            style={{ padding: '8px 10px', border: '1px solid #86efac', borderRadius: '6px', fontSize: '13px', flex: 1, minWidth: '220px' }}
            placeholder="Start typing an address…"
            value={allPlace.address}
            onChange={(v) => setAllPlace((s) => ({ ...s, address: v, lat: null, lng: null }))}
            onPick={(p) => setAllPlace((s) => ({ ...s, address: p.description, lat: p.lat, lng: p.lng }))}
            onEnter={addPlaceAllCars} />
          <button onClick={addPlaceAllCars} disabled={allBusy || !allPlace.address.trim()}
            style={{ background: allBusy || !allPlace.address.trim() ? '#94a3b8' : 'linear-gradient(135deg,#16a34a,#15803d)', color: 'white', fontWeight: 700, fontSize: '13px', border: 'none', borderRadius: '8px', padding: '9px 16px', cursor: allBusy || !allPlace.address.trim() ? 'default' : 'pointer' }}>
            {allBusy ? 'Adding…' : `Add to all ${devices.length} cars`}
          </button>
        </div>
        {allMsg && <div style={{ fontSize: '12px', fontWeight: 600, color: allMsg.startsWith('✅') ? '#15803d' : '#b91c1c', marginTop: '8px' }}>{allMsg}</div>}

        {/* list of places already added */}
        {placeGroups.length > 0 && (
          <div style={{ marginTop: '12px', borderTop: `1px solid ${GREEN_BD}`, paddingTop: '10px' }}>
            <div style={{ fontSize: '10px', fontWeight: 700, color: '#166534', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '6px' }}>Saved places ({placeGroups.length})</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {placeGroups.map((grp) => (
                <span key={grp.name} style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', background: CHIP_BG, border: `1px solid ${GREEN_BD}`, borderRadius: '999px', padding: '4px 7px 4px 11px', fontSize: '12px', fontWeight: 600, color: TILE_TXT }}>
                  ⭐ {grp.name}
                  <span style={{ fontSize: '10px', fontWeight: 700, color: '#15803d', background: '#dcfce7', borderRadius: '999px', padding: '1px 7px' }}>{grp.count} car{grp.count === 1 ? '' : 's'}</span>
                  <button onClick={() => removePlaceGroup(grp.name)} title="Remove from all cars" style={{ background: 'transparent', color: TILE_MUTED, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '14px', lineHeight: 1, padding: '0 2px' }}>×</button>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* app-icon layout — vehicles line up like icons on a desktop, wrapping as devices are added */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 22px', alignItems: 'flex-start' }}>
        {devices.filter((d) => {
          if (!search.trim()) return true;
          const p = positions.find((x) => x.deviceId === d.id);
          const drv = drivers.find((x) => String((x.attributes || {}).currentUnit) === String(d.id));
          const parts = {
            name: `${(d.attributes || {}).displayName || ''} ${d.name}`,
            vin: (p && p.attributes && p.attributes.vin) || '',
            imei: d.uniqueId || '',
            driver: drv ? drv.name : '',
            status: d.status || '',
          };
          const hay = (searchField === 'all' ? Object.values(parts).join(' ') : parts[searchField] || '').toLowerCase();
          return hay.includes(search.trim().toLowerCase());
        }).map((d) => {
          const chip = statusChip(liveStatus(d));
          const pos = positions.find((p) => p.deviceId === d.id);
          const a = (pos && pos.attributes) || {};
          const mph = pos ? Math.round((pos.speed || 0) * 1.15078) : 0;
          const pct = a.batteryLevel != null ? a.batteryLevel
            : (a.battery != null ? Math.round(Math.min(100, Math.max(0, (a.battery - 3.5) / (4.2 - 3.5) * 100))) : null);
          const miniChip = (bg, fg, txt) => (
            <span style={{ background: bg, color: fg, borderRadius: '3px', padding: '0px 4px', fontSize: '8px', fontWeight: 700, whiteSpace: 'nowrap' }}>{txt}</span>
          );
          return (
            <div
              key={d.id}
              onClick={() => openProfile(d.id)}
              title={`Tracker: ${chip.text}`}
              style={{ width: '124px', textAlign: 'center', cursor: 'pointer', padding: '6px 4px', borderRadius: '10px' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(0,102,255,0.06)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <div style={{ position: 'relative', width: '76px', height: '76px', margin: '0 auto 6px' }}>
                <div style={{
                  width: '76px', height: '76px', borderRadius: '16px',
                  background: thumbs[d.id]
                    ? `url(${thumbs[d.id]}) center/cover`
                    : 'linear-gradient(135deg, #00c8ff, #0066ff)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '32px',
                  boxShadow: '0 4px 10px rgba(15,23,42,0.2)', border: '1px solid rgba(255,255,255,0.6)',
                }}>
                  {!thumbs[d.id] && '🚘'}
                </div>
                {/* tiny tracker-status dot on the tile corner */}
                <span title={`Tracker: ${chip.text}`} style={{ position: 'absolute', top: '-2px', right: '-2px', width: '15px', height: '15px', borderRadius: '50%', background: chip.color, border: '2px solid white' }}></span>
                {/* pending broker/family approval marker */}
                {(pendingLinks(d).length + pendingMemberLinks(d).length) > 0 && (
                  <span title="Approval pending (broker or family)" style={{ position: 'absolute', top: '-6px', left: '-6px', background: '#7c3aed', color: 'white', fontSize: '9px', fontWeight: 800, borderRadius: '999px', padding: '1px 5px', border: '2px solid white', boxShadow: '0 2px 6px rgba(124,58,237,0.5)' }}>🔔 {pendingLinks(d).length + pendingMemberLinks(d).length}</span>
                )}
              </div>
              <div style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '12px', color: TILE_TXT, lineHeight: 1.15, wordBreak: 'break-word' }}>{(d.attributes || {}).displayName || d.name}</div>
              {(d.attributes || {}).displayName && <div style={{ fontSize: '9px', color: TILE_MUTED, fontWeight: 500, lineHeight: 1.2 }}>#{d.name}</div>}
              {/* one-line vitals */}
              {pos && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px', justifyContent: 'center', marginTop: '4px' }}>
                  {miniChip(a.ignition ? '#22c55e' : '#ef4444', '#fff', a.ignition ? 'ON' : 'OFF')}
                  {miniChip('#e5e7eb', '#111', `${mph}mph`)}
                  {a.io48 != null && miniChip('#0ea5e9', '#fff', `⛽${a.io48}%`)}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* profile popup */}
      {historyDevice && <VehicleHistory device={historyDevice} onClose={() => { playClose(); setHistoryDevice(null); }} />}

      {openId && device && (
        <div
          onClick={closeProfile}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(10,26,47,0.55)', zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white', borderRadius: '16px', width: '100%', maxWidth: '640px',
              maxHeight: '88vh', overflowY: 'auto', padding: '24px', boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                {renaming ? (
                  <input autoFocus value={nameDraft} onChange={(e) => setNameDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') saveDeviceName(); if (e.key === 'Escape') setRenaming(false); }} onBlur={saveDeviceName}
                    style={{ fontFamily: 'Poppins, sans-serif', fontSize: '19px', fontWeight: 700, color: TXT, border: '1px solid #cbd5e1', borderRadius: '8px', padding: '2px 10px', width: '260px' }} />
                ) : (
                  <>
                    <h2 style={{ margin: 0, fontFamily: 'Poppins, sans-serif', fontSize: '20px', color: TXT }}>{(device.attributes || {}).displayName || device.name}</h2>
                    {(device.attributes || {}).displayName && <span style={{ fontSize: '12px', fontWeight: 600, color: TXT_MUTED }} title="Device reference (unchanged in the system)">#{device.name}</span>}
                    <button onClick={() => { setNameDraft((device.attributes || {}).displayName || ''); setRenaming(true); }} title="Rename this device" style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '15px', lineHeight: 1, padding: 0 }}>✏️</button>
                  </>
                )}
              </div>
              <span style={{
                fontSize: '11px', fontWeight: 700, padding: '3px 12px', borderRadius: '999px',
                background: statusChip(liveStatus(device)).bg, color: statusChip(liveStatus(device)).color,
              }}>{statusChip(liveStatus(device)).text}</span>
              <button onClick={() => { playOpen(); setHistoryDevice(device); }} style={{ background: 'linear-gradient(135deg, #0066ff, #0047d0)', color: 'white', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: 700 }}>📅 Trip History</button>
              <button onClick={closeProfile} style={{ background: '#f1f5f9', borderRadius: '50%', width: '30px', height: '30px', padding: 0, fontSize: '16px', color: '#475569' }}>×</button>
            </div>

            {/* profile info */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 20px', marginBottom: '20px', fontSize: '13px' }}>
              {[
                ['IMEI', device.uniqueId],
                ['SIM (ICCID)', (() => { const icc = a.iccid || a.icc || a.iccid1 || (device.attributes || {}).simIccid; return icc ? `···${String(icc).slice(-6)}` : null; })()],
                ['Odometer', a.io389 != null ? `${Math.round(a.io389 * 0.621371).toLocaleString()} mi` : null],
                ['VIN', a.vin],
                ['Fuel level', a.io48 != null ? `${a.io48}%` : null],
                ['Battery', a.power != null ? `${a.power.toFixed(1)} V` : null],
                ['Ignition', a.ignition != null ? (a.ignition ? 'On' : 'Off') : null],
                ['Last report', position ? new Date(position.fixTime).toLocaleString() : null],
                ['Fault codes', a.io30 != null ? (a.io30 === 0 ? 'None' : a.io30) : null],
              ].filter(([, v]) => v != null).map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', padding: '4px 0' }}>
                  <span style={{ color: '#64748b' }}>{k}</span>
                  <span style={{ fontWeight: 600, color: TXT }}>{v}</span>
                </div>
              ))}
            </div>

            {/* vehicle details — shared with the customer's device profile */}
            <div style={{ border: '1px solid #e9d5ff', borderRadius: '10px', padding: '12px', marginBottom: '16px', background: '#faf7ff' }}>
              <div style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: '13px', color: '#7c3aed', marginBottom: '8px' }}>🚘 Vehicle details</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '8px' }}>
                {[
                  ['vehYear', 'Year', VEHICLE_YEARS],
                  ['vehMake', 'Make', VEHICLE_MAKES],
                  ['vehModel', 'Model', modelsForMake((device.attributes || {}).vehMake)],
                  ['vehColor', 'Color', VEHICLE_COLORS],
                ].map(([k, label, options]) => (
                  <div key={k}>
                    <span style={{ fontSize: '10px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: '3px' }}>{label}</span>
                    <input list={`opts-${k}`} defaultValue={(device.attributes || {})[k] || ''} onBlur={(e) => saveDeviceAttr({ [k]: e.target.value })}
                      style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', width: '100%', boxSizing: 'border-box' }} />
                    <datalist id={`opts-${k}`}>
                      {options.map((o) => <option key={o} value={o} />)}
                    </datalist>
                  </div>
                ))}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '8px' }}>
                <div>
                  <span style={{ fontSize: '10px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: '3px' }}>License plate</span>
                  <input defaultValue={(device.attributes || {}).vehPlate || ''} onBlur={(e) => saveDeviceAttr({ vehPlate: e.target.value.toUpperCase() })}
                    style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', width: '100%', boxSizing: 'border-box', textTransform: 'uppercase', fontWeight: 700 }} placeholder="ABC1234" />
                </div>
                <div>
                  <span style={{ fontSize: '10px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: '3px' }}>Registration expires</span>
                  <input type="date" defaultValue={(device.attributes || {}).vehPlateExp || ''} onBlur={(e) => saveDeviceAttr({ vehPlateExp: e.target.value })}
                    style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', width: '100%', boxSizing: 'border-box' }} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '8px' }}>
                <div>
                  <span style={{ fontSize: '10px', fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: '3px' }}>👤 Assigned driver</span>
                  <input key={`ad-${device.id}-${(device.attributes || {}).assignedDriver || ''}`} defaultValue={(device.attributes || {}).assignedDriver || ''} onBlur={(e) => saveDeviceAttr({ assignedDriver: e.target.value })}
                    style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', width: '100%', boxSizing: 'border-box' }} placeholder="Driver's name" />
                </div>
                <div>
                  <span style={{ fontSize: '10px', fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: '3px' }}>Driver's license #</span>
                  <input key={`adl-${device.id}-${(device.attributes || {}).assignedDriverLicense || ''}`} defaultValue={(device.attributes || {}).assignedDriverLicense || ''} onBlur={(e) => saveDeviceAttr({ assignedDriverLicense: e.target.value })}
                    style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', width: '100%', boxSizing: 'border-box' }} placeholder="Optional" />
                </div>
              </div>
              <p style={{ fontSize: '11px', color: '#7c3aed', margin: '8px 0 0' }}>🔔 An alert fires a month before the registration expires. The assigned driver stays set until you change it — during an active rental it's set to the renter automatically, then cleared when the rental ends. These fields sync with the customer's device profile.</p>
            </div>

            {/* rentals toggle — only customers who rent this car out see rental options */}
            <div style={{ border: '1px solid #fed7aa', borderRadius: '10px', padding: '12px', marginBottom: '16px', background: '#fff7ed' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <input type="checkbox" checked={!!getRentalSettings(device.id).enabled} onChange={(e) => toggleRental(device, e.target.checked)} style={{ width: '18px', height: '18px', flexShrink: 0, accentColor: '#c2410c' }} />
                <span>
                  <span style={{ fontWeight: 800, fontSize: '13px', color: '#c2410c', display: 'block' }}>🚗 This vehicle is used for rentals</span>
                  <span style={{ fontSize: '11px', color: '#9a3412' }}>{getRentalSettings(device.id).enabled ? 'Rental options are on — manage pricing, agreements and payments in the Rentals tab.' : 'Off — rental options stay hidden. Turn this on only if you rent this car out.'}</span>
                </span>
              </label>
            </div>

            {/* saved places (⭐ geofences) — personal to THIS car */}
            <div style={{ border: `1px solid ${GREEN_BD}`, borderRadius: '14px', padding: '12px', marginBottom: '16px', background: GREEN_BG }}>
              <div style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: '13px', color: '#15803d', marginBottom: '2px' }}>📍 Saved places for this car</div>
              <div style={{ fontSize: '11px', color: '#166534', marginBottom: '9px' }}>Add an address (Home, School, Office…). You’ll get an “arrived” alert when this car parks there, and can ask it “did it get to School today?”. These belong to this car only.</div>
              {devPlaces.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
                  {devPlaces.map((g) => (
                    <span key={g.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: CHIP_BG, border: `1px solid ${GREEN_BD}`, borderRadius: '999px', padding: '4px 6px 4px 11px', fontSize: '12px', fontWeight: 600, color: TXT }}>
                      ⭐ {placeName(g)}
                      <button onClick={() => removeCarPlace(g)} title="Remove this place" style={{ background: 'transparent', color: TXT_MUTED, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '14px', lineHeight: 1, padding: '0 2px' }}>×</button>
                    </span>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                <input style={{ padding: '8px 10px', border: '1px solid #86efac', borderRadius: '6px', fontSize: '13px', width: '130px' }}
                  placeholder="Name" value={placeInput.name} onChange={(e) => setPlaceInput((s) => ({ ...s, name: e.target.value }))} />
                <AddressAutocomplete
                  style={{ padding: '8px 10px', border: '1px solid #86efac', borderRadius: '6px', fontSize: '13px', flex: 1, minWidth: '180px' }}
                  placeholder="Start typing an address…"
                  value={placeInput.address}
                  onChange={(v) => setPlaceInput((s) => ({ ...s, address: v, lat: null, lng: null }))}
                  onPick={(p) => setPlaceInput((s) => ({ ...s, address: p.description, lat: p.lat, lng: p.lng }))}
                  onEnter={addCarPlace} />
                <button onClick={addCarPlace} disabled={placeBusy || !placeInput.address.trim()}
                  style={{ background: placeBusy || !placeInput.address.trim() ? '#94a3b8' : 'linear-gradient(135deg,#16a34a,#15803d)', color: 'white', fontWeight: 700, fontSize: '13px', border: 'none', borderRadius: '8px', padding: '9px 15px', cursor: placeBusy || !placeInput.address.trim() ? 'default' : 'pointer' }}>
                  {placeBusy ? 'Adding…' : 'Add'}
                </button>
              </div>
            </div>

            {/* broker share code + QR — only when this car is set up for rentals
                (brokers are part of the rental process; hidden for non-rental cars) */}
            {getRentalSettings(device.id).enabled && (
            <div style={{ border: '1px solid #bfdbfe', borderRadius: '10px', padding: '12px', marginBottom: '16px', background: '#eff6ff', display: 'flex', gap: '14px', alignItems: 'center', flexWrap: 'wrap' }}>
              <img src={qrUrlFor(codeForDevice(device), 120)} alt="broker QR" width="108" height="108" title="Click to enlarge" onClick={() => setQrBig(true)} style={{ borderRadius: '8px', background: 'white', border: '1px solid #dbeafe', cursor: 'zoom-in' }} />
              <div style={{ flex: 1, minWidth: '200px' }}>
                <div style={{ fontWeight: 800, fontSize: '13px', color: '#1e3a8a', marginBottom: '2px' }}>🚗 Broker share code</div>
                <div style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '18px', letterSpacing: '1px', color: TXT }}>{codeForDevice(device)}</div>
                <div style={{ fontSize: '11px', color: TXT, marginTop: '6px' }}>Give this code (or the QR) to a broker so they can add <b>this car</b> to their fleet. Each car has its own code.</div>
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
                  <button onClick={() => { try { navigator.clipboard.writeText(codeForDevice(device)); } catch { /* ignore */ } }} style={{ background: '#1d4ed8', color: 'white', border: 'none', borderRadius: '7px', padding: '5px 12px', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>Copy code</button>
                  <button onClick={() => shareCarCode(device)} style={{ background: '#16a34a', color: 'white', border: 'none', borderRadius: '7px', padding: '5px 12px', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>📤 Share QR</button>
                  <button onClick={() => setQrBig(true)} style={{ background: '#e2e8f0', color: TXT, border: 'none', borderRadius: '7px', padding: '5px 12px', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>🔍 Enlarge</button>
                </div>
              </div>
            </div>
            )}
            {qrBig && (
              <div onClick={() => setQrBig(false)} style={{ position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(15,23,42,0.78)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
                <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '16px', padding: '22px', textAlign: 'center', maxWidth: '92vw' }}>
                  <div style={{ fontWeight: 800, fontSize: '15px', marginBottom: '10px', color: TXT }}>Scan to add {(device.attributes || {}).displayName || device.name}</div>
                  <img src={qrUrlFor(codeForDevice(device), 320)} alt="broker QR large" width="300" height="300" style={{ borderRadius: '10px', border: '1px solid #e2e8f0', maxWidth: '80vw', height: 'auto' }} />
                  <div style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '22px', letterSpacing: '2px', color: TXT, margin: '12px 0 4px' }}>{codeForDevice(device)}</div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '12px' }}>The broker scans this or enters the code to add this car.</div>
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                    <button onClick={() => shareCarCode(device)} style={{ background: '#16a34a', color: 'white', border: 'none', borderRadius: '8px', padding: '8px 16px', fontWeight: 700, cursor: 'pointer' }}>📤 Share</button>
                    <button onClick={() => setQrBig(false)} style={{ background: '#e2e8f0', color: TXT, border: 'none', borderRadius: '8px', padding: '8px 16px', fontWeight: 700, cursor: 'pointer' }}>Close</button>
                  </div>
                </div>
              </div>
            )}

            {/* broker link requests — only for rental cars (brokers = rental process) */}
            {/* broker requests (rental cars) and fleet-company requests (any car) wait here for the owner */}
            {(getRentalSettings(device.id).enabled || getBrokerLinks(device).some((l) => l.kind === 'fleet')) && getBrokerLinks(device).length > 0 && (
              <div style={{ border: '1px solid #fde68a', borderRadius: '10px', padding: '12px', marginBottom: '16px', background: '#fffbeb' }}>
                <div style={{ fontWeight: 800, fontSize: '13px', color: '#b45309', marginBottom: '8px' }}>🤝 Broker & fleet requests</div>
                {getBrokerLinks(device).map((lnk) => (
                  <div key={lnk.brokerId} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', borderRadius: '8px', background: lnk.status === 'pending' ? '#fffbeb' : lnk.status === 'approved' ? '#f0fdf4' : '#f8fafc', border: `1px solid ${lnk.status === 'pending' ? '#fde68a' : lnk.status === 'approved' ? '#bbf7d0' : '#e2e8f0'}`, marginBottom: '6px', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: '150px' }}>
                      <div style={{ fontWeight: 700, fontSize: '13px', color: TXT }}>{lnk.kind === 'fleet' ? '🏢 Fleet company: ' : ''}{lnk.brokerName}{lnk.brokerCompany && lnk.brokerCompany !== lnk.brokerName ? ` · ${lnk.brokerCompany}` : ''}</div>
                      <div style={{ fontSize: '11px', color: TXT_MUTED }}>{lnk.brokerEmail} · requested {new Date(lnk.at).toLocaleDateString()}</div>
                    </div>
                    {lnk.status === 'pending' ? (
                      <>
                        <button onClick={() => decideBroker(device, lnk.brokerId, 'approved')} style={{ background: '#16a34a', color: 'white', border: 'none', borderRadius: '7px', padding: '6px 12px', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>✓ Approve</button>
                        <button onClick={() => decideBroker(device, lnk.brokerId, 'declined')} style={{ background: '#fee2e2', color: '#b91c1c', border: 'none', borderRadius: '7px', padding: '6px 12px', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>Decline</button>
                      </>
                    ) : (
                      <>
                        <span style={{ fontSize: '10px', fontWeight: 800, padding: '3px 9px', borderRadius: '999px', background: lnk.status === 'approved' ? '#dcfce7' : '#f1f5f9', color: lnk.status === 'approved' ? '#15803d' : '#64748b' }}>{lnk.status === 'approved' ? 'APPROVED' : 'DECLINED'}</span>
                        {lnk.status === 'approved' && (
                          <button onClick={() => toggleBrokerTracking(device, lnk.brokerId, lnk.tracking === false)} title="Live tracking + alerts for this broker during rentals" style={{ background: lnk.tracking !== false ? '#dcfce7' : '#f1f5f9', color: lnk.tracking !== false ? '#15803d' : '#64748b', border: 'none', borderRadius: '999px', padding: '4px 10px', fontSize: '11px', fontWeight: 800, cursor: 'pointer' }}>📍 Tracking {lnk.tracking !== false ? 'ON' : 'OFF'}</button>
                        )}
                        <button onClick={() => revokeBroker(device, lnk.brokerId)} title="Remove access" style={{ background: 'transparent', border: 'none', color: TXT_MUTED, cursor: 'pointer', fontSize: '13px', fontWeight: 700 }}>Remove</button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* family / shared access — give the code/QR, approve each person for full view */}
            <div style={{ border: '1px solid #99f6e4', borderRadius: '10px', padding: '12px', marginBottom: '16px', background: '#f0fdfa' }}>
              <div style={{ fontWeight: 800, fontSize: '13px', color: '#0f766e', marginBottom: '2px' }}>👨‍👩‍👧 Family & shared access</div>
              <div style={{ fontSize: '11px', color: '#134e4a', marginBottom: '10px' }}>Give this car’s code or QR to a family member. They sign in at <b>/family</b>, and once you approve they see this car with the <b>same full access</b> you have.</div>
              <div style={{ display: 'flex', gap: '14px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '10px' }}>
                <img src={qrUrlForFamily(codeForDevice(device), 116)} alt="family QR" width="104" height="104" title="Family QR — scan to add this car" style={{ borderRadius: '8px', background: 'white', border: '1px solid #99f6e4' }} />
                <div style={{ flex: 1, minWidth: '180px' }}>
                  <div style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '18px', letterSpacing: '1px', color: TXT, marginBottom: '8px' }}>{codeForDevice(device)}</div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button onClick={() => { try { navigator.clipboard.writeText(codeForDevice(device)); } catch { /* ignore */ } }} style={{ background: '#0f766e', color: 'white', border: 'none', borderRadius: '7px', padding: '6px 12px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>Copy code</button>
                    <button onClick={() => { const url = familyAddUrl(codeForDevice(device)); if (navigator.share) navigator.share({ title: 'Add my car on TagAlong', text: `Add this car with code ${codeForDevice(device)}`, url }).catch(() => {}); else { try { navigator.clipboard.writeText(url); alert('Family link copied.'); } catch { /* ignore */ } } }} style={{ background: '#14b8a6', color: 'white', border: 'none', borderRadius: '7px', padding: '6px 12px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>🔗 Share link</button>
                  </div>
                </div>
              </div>
              {getMemberLinks(device).length === 0
                ? <div style={{ fontSize: '12px', color: TXT_MUTED }}>No family members added yet.</div>
                : getMemberLinks(device).map((lnk) => (
                  <div key={lnk.memberId} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', borderRadius: '8px', background: lnk.status === 'pending' ? '#fffbeb' : lnk.status === 'approved' ? '#f0fdf4' : '#f8fafc', border: `1px solid ${lnk.status === 'pending' ? '#fde68a' : lnk.status === 'approved' ? '#bbf7d0' : '#e2e8f0'}`, marginBottom: '6px', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: '150px' }}>
                      <div style={{ fontWeight: 700, fontSize: '13px', color: TXT }}>{lnk.memberName}</div>
                      <div style={{ fontSize: '11px', color: TXT_MUTED }}>{lnk.memberEmail} · requested {new Date(lnk.at).toLocaleDateString()}</div>
                    </div>
                    {lnk.status === 'pending' ? (
                      <>
                        <button onClick={() => decideMember(device, lnk.memberId, 'approved')} style={{ background: '#16a34a', color: 'white', border: 'none', borderRadius: '7px', padding: '6px 12px', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>✓ Approve</button>
                        <button onClick={() => decideMember(device, lnk.memberId, 'declined')} style={{ background: '#fee2e2', color: '#b91c1c', border: 'none', borderRadius: '7px', padding: '6px 12px', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>Decline</button>
                      </>
                    ) : (
                      <>
                        <span style={{ fontSize: '10px', fontWeight: 800, padding: '3px 9px', borderRadius: '999px', background: lnk.status === 'approved' ? '#ccfbf1' : '#f1f5f9', color: lnk.status === 'approved' ? '#0f766e' : '#64748b' }}>{lnk.status === 'approved' ? 'FULL ACCESS' : 'DECLINED'}</span>
                        <button onClick={() => revokeMember(device, lnk.memberId)} title="Remove access" style={{ background: 'transparent', border: 'none', color: TXT_MUTED, cursor: 'pointer', fontSize: '13px', fontWeight: 700 }}>Remove</button>
                      </>
                    )}
                  </div>
                ))}
            </div>

            {/* speed alert thresholds */}
            <div style={{ border: '1px solid #fecaca', borderRadius: '10px', padding: '12px', marginBottom: '16px', background: '#fef2f2' }}>
              <div style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: '13px', color: '#b91c1c', marginBottom: '8px' }}>🚦 Speed Alerts</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <span style={{ fontSize: '11px', fontWeight: 700, color: '#b45309', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: '3px' }}>⏩ Speeding warning (mph)</span>
                  <input type="number" placeholder="e.g. 70" defaultValue={(device.attributes || {}).speedWarnMph || ''} onBlur={(e) => saveDeviceAttr({ speedWarnMph: Number(e.target.value) || null })}
                    style={{ padding: '8px 10px', border: '1px solid #f59e0b', borderRadius: '6px', fontSize: '13px', width: '100%', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <span style={{ fontSize: '11px', fontWeight: 700, color: '#b91c1c', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: '3px' }}>🚨 Over-speed (mph)</span>
                  <input type="number" placeholder="e.g. 80" defaultValue={(device.attributes || {}).speedMaxMph || ''} onBlur={(e) => saveDeviceAttr({ speedMaxMph: Number(e.target.value) || null })}
                    style={{ padding: '8px 10px', border: '1px solid #ef4444', borderRadius: '6px', fontSize: '13px', width: '100%', boxSizing: 'border-box' }} />
                </div>
              </div>
              <p style={{ fontSize: '11px', color: TXT_MUTED, margin: '8px 0 0' }}>Amber warning at the first threshold, red alert at the over-speed threshold. Saves to the vehicle; leave blank to disable.</p>
            </div>

            {/* engine / RPM alert */}
            <div style={{ border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px', marginBottom: '16px' }}>
              <div style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: '13px', color: TXT, marginBottom: '8px' }}>🔧 Aggressive RPM Alert</div>
              <div style={{ maxWidth: '220px' }}>
                <span style={{ fontSize: '11px', fontWeight: 700, color: '#b45309', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: '3px' }}>High RPM threshold (rpm)</span>
                <input type="number" placeholder="e.g. 4000" defaultValue={(device.attributes || {}).rpmAlertRpm || ''} onBlur={(e) => saveDeviceAttr({ rpmAlertRpm: Number(e.target.value) || null })}
                  style={{ padding: '8px 10px', border: '1px solid #f59e0b', borderRadius: '6px', fontSize: '13px', width: '100%', boxSizing: 'border-box' }} />
              </div>
              <p style={{ fontSize: '11px', color: TXT_MUTED, margin: '8px 0 0' }}>Alerts when the engine is revved past this RPM — flags aggressive acceleration / hard revving. Most cars cruise under 2,500 rpm; 4,000+ is heavy acceleration. Leave blank to disable.</p>
            </div>

            {/* photos */}
            <h3 style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', color: TXT, margin: '0 0 8px' }}>📷 Exterior</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginBottom: '16px' }}>
              {SIDE_SLOTS.map((s) => (
                <PhotoSlot key={s} label={SLOT_LABELS[s]} photo={photos[s]}
                  onUpload={(d) => upload(s, d)} onRemove={() => remove(s)} />
              ))}
            </div>

            <h3 style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', color: TXT, margin: '0 0 8px' }}>🪑 Inside (up to 4)</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
              {INSIDE_SLOTS.map((s) => (
                <PhotoSlot key={s} label={SLOT_LABELS[s]} photo={photos[s]}
                  onUpload={(d) => upload(s, d)} onRemove={() => remove(s)} />
              ))}
            </div>

            <p style={{ fontSize: '11px', color: TXT_MUTED, marginTop: '14px' }}>
              Photos are stored in this browser for now — they'll move to the server when the dashboard is deployed.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
